import {
  Button,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { type AdminEnhancedDomain, type AdminModel, createAdminRecipe } from 'higgins-domain'
import { type MouseEvent, useCallback, useState } from 'react'

import { DeleteEntityMenuItem, EditEntityMenuItem, EntityRow } from '@salsita-ai/platform-project-sdk/admin'
import { useSelectEntity } from '@salsita-ai/platform-project-sdk/react'

import { adminInterface } from '../../systems/admin-interface'

type MaterialEntity = 'CabinetColor' | 'CountertopColor'
type MaterialId = AdminModel.CabinetMaterial['id'] | AdminModel.CountertopMaterial['id']

export const MaterialsPage = (props: { entity: MaterialEntity }) => {
  const { entity } = props

  const $root = adminInterface.useRootEntityState()
  const rootEntityId = adminInterface.useRootEntityId()

  const isCabinet = entity === 'CabinetColor'

  const { disabled: disabledColor, create: createColor } = adminInterface.useCookedRecipes(
    [
      createAdminRecipe({
        type: isCabinet ? 'CabinetSolidColorMaterial' : 'CountertopSolidColorMaterial',
        name: isCabinet ? 'New cabinet color' : 'New countertop color',
        color: '#FFFFFF',
      }),
    ],
    rootEntityId
  )

  const { disabled: disabledWood, create: createWood } = adminInterface.useCookedRecipes(
    [
      createAdminRecipe(
        isCabinet
          ? {
              type: 'CabinetWoodTextureMaterial',
              name: 'New cabinet wood texture',
              materialName: 'lightWoodMaterial',
            }
          : {
              type: 'CountertopWoodTextureMaterial',
              name: 'New countertop wood texture',
              materialName: 'butcherBlockMaterial',
            }
      ),
    ],
    rootEntityId
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleOpenMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center">
        <Button variant="contained" size="small" onClick={handleOpenMenu} disabled={disabledColor && disabledWood}>
          Add Material
        </Button>
        <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={handleCloseMenu}>
          <MenuItem
            onClick={() => {
              handleCloseMenu()
              createColor()
            }}
            disabled={disabledColor}
          >
            Add Color
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleCloseMenu()
              createWood()
            }}
            disabled={disabledWood}
          >
            Add Wood Texture
          </MenuItem>
        </Menu>
      </Stack>
      <TableContainer>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10ch' }}>Preview</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Hex Color</TableCell>
              <TableCell sx={{ width: 62 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isCabinet
              ? $root.$cabinetMaterials.map((color, index) => (
                  <MaterialsRow key={color.id} index={index} color={color} />
                ))
              : $root.$countertopMaterials.map((color, index) => (
                  <MaterialsRow key={color.id} index={index} color={color} />
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

const MaterialsAction = (props: { id: MaterialId; onClose: () => void }) => {
  const { id, onClose } = props

  return (
    <>
      <EditEntityMenuItem id={id} onClose={onClose} title="Edit color" bold />
      <DeleteEntityMenuItem id={id} onClose={onClose} />
    </>
  )
}

const MaterialsRow = (props: { index: number; color: AdminEnhancedDomain.$Material }) => {
  const { index, color } = props

  const id = color.id
  const selectEntity = useSelectEntity()

  const handleClick = useCallback(() => {
    selectEntity(id)
  }, [id, selectEntity])

  return (
    <>
      <EntityRow.Root
        key={id}
        id={id}
        index={index}
        onClick={handleClick}
        name={color.$name}
        description={color.$description}
        thumbnailUrl={color.$swatchUrl}
        Actions={MaterialsAction}
      />
    </>
  )
}
